import { useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { DEAL_CARD } from '../lib/card-fields.ts';
import { RecordList } from '../components/boards/record-list.tsx';
import {
  AddToPipelineDialog,
  BulkPipelineBar,
  describeAddResult,
} from '../components/pipelines/add-to-pipeline-bulk.tsx';
import { PipelinePicker } from '../components/pipelines/pipeline-picker.tsx';
import { StagePill } from '../components/ui/tone-pill.tsx';
import { type ColumnDef } from '../components/record-table/index.ts';
import { Gate } from '../components/shell/gate.tsx';
import { api, type DealView } from '../lib/client.ts';
import { andFilter, flatConditions, useListView } from '../lib/list-view.ts';
import { useVisibleDefinitions } from '../lib/custom-fields.ts';
import { dealFilterFields } from '../lib/list-filters.ts';
import { pickedPipeline, placeList, stageInPlace } from '../lib/place-lists.ts';
import { BILLING_LABEL, usePipelines } from '../lib/record-data.ts';
import { useSession } from '../lib/session.tsx';
import { PageHeader } from '../components/ui/page-header.tsx';

/** Deals in one Deal Flow pipeline: search, totals, and a move through `:move-status`. */
const dealList = (pipelineId: string | undefined) =>
  placeList<DealView>(pipelineId, {
    search: (body) => api.deals.search(body),
    counts: (body) => api.deals.counts(body),
    move: (id, stageId) => api.deals.moveStatus(id, stageId),
    titleOf: (d) => d.title,
    sortable: ['title', 'value', 'expectedCloseDate', 'paidAt', 'createdAt', 'updatedAt'],
  });

/**
 * Deals across every project (RELAY-1038): the payment lines, as a table or as a board by
 * Deal Status — the same list either way. Opening a deal goes to its project's Deals tab.
 *
 * Like Projects, the list shows ONE Deal Flow pipeline, picked beside the toolbar and kept in the
 * view state: the default lists every deal by its status; an extra one lists only the deals in it,
 * at their place there. Ticked rows can be added to an extra pipeline; stages never change in bulk.
 */
export function DealsPage() {
  const session = useSession();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const search = useSearch({ from: '/authed/deals' });
  const pipelines = usePipelines('deal');
  const definitions = useVisibleDefinitions('deals');
  const write = useCallback(
    (view: string) => void navigate({ to: '/deals', search: { view }, replace: true }),
    [navigate],
  );
  const { state, setState, activeViewId } = useListView({
    entity: 'deals',
    remember: 'deals',
    encoded: search.view,
    write,
  });
  const conditions = useMemo(() => flatConditions(state.filter), [state.filter]);
  const pipeline = pickedPipeline(pipelines, state.pipelineId);
  const pipelineId = pipeline && !pipeline.isDefault ? pipeline.id : undefined;
  const listApi = useMemo(() => dealList(pipelineId), [pipelineId]);
  const pickPipeline = (id: string) => {
    const next = pipelines.find((p) => p.id === id);
    // A stage filter names a stage of the pipeline it was set in: it goes with it.
    setState({
      ...state,
      pipelineId: next && !next.isDefault ? next.id : undefined,
      filter: andFilter(conditions.filter((c) => c.field !== 'stageId')),
    });
  };

  // Bulk: tick rows in the table, then add them to an extra pipeline.
  const canUpdate = session.can('customer.update');
  const extras = useMemo(() => pipelines.filter((p) => !p.isDefault), [pipelines]);
  const [selected, setSelected] = useState<Set<string>>(() => new Set());
  const [adding, setAdding] = useState<string[] | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const tableLayout = !state.layout || state.layout === 'table';
  const selectionScope = JSON.stringify([
    state.filter ?? null,
    state.q ?? null,
    pipelineId,
    state.layout,
  ]);
  useEffect(() => setSelected(new Set()), [selectionScope]);

  const filterFields = useMemo(
    () => dealFilterFields({ definitions, search: listApi.search, pipeline }),
    [definitions, listApi, pipeline],
  );
  const columns = useMemo<ColumnDef<DealView, unknown>[]>(
    () => [
      { accessorKey: 'title', header: 'Deal', size: 220 },
      {
        id: 'project',
        header: 'Project',
        size: 240,
        enableSorting: false,
        cell: ({ row }) => `${row.original.project.name} · ${row.original.project.companyName}`,
      },
      {
        id: 'stage',
        header: pipeline?.name ?? 'Deal Flow',
        size: 170,
        // Measured by the stage's name; the pill adds its padding.
        accessorFn: (d) => stageInPlace(d, pipelineId)?.name ?? '',
        meta: { autoSize: 24 },
        enableSorting: false,
        cell: ({ row }) => {
          const stage = stageInPlace(row.original, pipelineId);
          if (!stage) return <span className="text-muted-foreground">—</span>;
          return <StagePill name={stage.name} terminalKind={stage.terminalKind} />;
        },
      },
      {
        id: 'value',
        accessorKey: 'value',
        header: 'Value',
        size: 130,
        cell: ({ row }) =>
          `${Number(row.original.value).toLocaleString()} ${row.original.currency}`,
      },
      {
        id: 'billing',
        header: 'Billing',
        size: 130,
        enableSorting: false,
        cell: ({ row }) => BILLING_LABEL[row.original.billingModel],
      },
      {
        accessorKey: 'expectedCloseDate',
        header: 'Expected close',
        size: 140,
        cell: ({ row }) => row.original.expectedCloseDate ?? '—',
      },
    ],
    [pipeline, pipelineId],
  );
  return (
    <Gate permission="customer.read" feature="customers.enabled">
      <PageHeader
        title="Deals"
        description="Payment lines across every project, by Deal Flow."
        actions={
          notice && (
            <span className="text-muted-foreground text-sm" role="status">
              {notice}
            </span>
          )
        }
      />
      {canUpdate && extras.length > 0 && tableLayout && selected.size > 0 && (
        <div className="mb-3">
          <BulkPipelineBar
            count={selected.size}
            onAdd={() => setAdding([...selected])}
            onClear={() => setSelected(new Set())}
          />
        </div>
      )}
      {pipeline ? (
        <RecordList
          key={pipeline.id}
          entity="deals"
          state={state}
          setState={setState}
          activeViewId={activeViewId}
          filter={state.filter}
          conditions={conditions}
          onConditionsChange={(next) => setState({ ...state, filter: andFilter(next) })}
          filterFields={filterFields}
          quickFilters={
            pipelines.length > 1 && (
              <PipelinePicker pipelines={pipelines} value={pipeline.id} onChange={pickPipeline} />
            )
          }
          api={listApi}
          columns={columns}
          pipeline={pipeline}
          cardSet={DEAL_CARD}
          canMove={canUpdate}
          onOpen={(d) =>
            void navigate({
              to: '/projects/$id',
              params: { id: d.projectId },
              search: { tab: 'deals' },
            })
          }
          emptyMessage="No deals."
          searchPlaceholder="Search deal titles…"
          {...(canUpdate && extras.length > 0
            ? { selection: selected, onSelectionChange: setSelected }
            : {})}
        />
      ) : (
        <p className="text-muted-foreground text-sm">Loading…</p>
      )}
      {adding !== null && (
        <AddToPipelineDialog
          noun="deal"
          ids={adding}
          pipelines={extras}
          current={pipeline?.id}
          add={(body) => api.deals.addManyToPipeline(body)}
          onClose={() => setAdding(null)}
          onDone={async (result) => {
            setAdding(null);
            setSelected(new Set());
            setNotice(describeAddResult(result));
            await Promise.all(
              [['records'], ['board', 'deals'], ['project']].map((queryKey) =>
                qc.invalidateQueries({ queryKey }),
              ),
            );
          }}
        />
      )}
    </Gate>
  );
}
